import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EntityList from '../components/EntityList/EntityList';
import ChipBuilder from '../components/ChipBuilder';
import AddButton from '../components/AddButton/AddButton';
import { ExportCSVButton } from '../components/ExportButtons';
import {
  listBlocks,
  archiveBlock,
  updateBlock,
  listAllGolfCourses,
  isAdmin as checkAdmin,
  exportBlocksCSV,
} from '../api';
import { LIST_PAGE_SIZE } from '../constants';
import useEntityList from '../hooks/useEntityList';
import { formatDateTime } from '../utils';

// ---------------------------------------------------------------------------
// Duration helper — "2h 30m" / "45m"
// ---------------------------------------------------------------------------

function computeDuration(startIso, endIso) {
  if (!startIso || !endIso) return '—';
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '—';
  const diffMins = Math.round((end - start) / 60000);
  if (diffMins <= 0) return '—';
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const COLUMNS = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'block_type_name',
    label: 'Block type',
  },
  {
    key: 'golf_course_name',
    label: 'Golf course',
  },
  {
    key: 'start_time',
    label: 'Start time',
    render: (value) => formatDateTime(value),
  },
  {
    key: 'end_time',
    label: 'End time',
    render: (value) => formatDateTime(value),
  },
  {
    key: 'duration',
    label: 'Duration',
    render: (_, item) => computeDuration(item.start_time, item.end_time),
  },
];

// ---------------------------------------------------------------------------
// Chip field definitions — dynamic options for Course and Block Type
// ---------------------------------------------------------------------------

const CHIP_FIELDS = [
  { key: 'name',           label: 'Name',         type: 'text', placeholder: 'Block name…' },
  { key: 'golf_course_id', label: 'Golf course',  type: 'select', options: null /* dynamic */ },
  { key: 'date_from',      label: 'Date from',    type: 'date' },
  { key: 'date_to',        label: 'Date to',      type: 'date' },
  {
    key: 'is_archived',
    label: 'Archived',
    type: 'select',
    options: [
      { value: 'false', label: 'Active' },
      { value: 'true',  label: 'Archived' },
    ],
  },
];

// ---------------------------------------------------------------------------
// BlocksPage
// ---------------------------------------------------------------------------

export default function BlocksPage({ user }) {
  const navigate = useNavigate();
  const isAdmin = checkAdmin(user);

  const [chips, setChips] = useState([]);
  const [exportError, setExportError] = useState(null);

  // Server-side filter values derived from chips
  const isArchived     = useMemo(() => chips.find((c) => c.field === 'is_archived')?.value === 'true', [chips]);
  const filterName     = useMemo(() => chips.find((c) => c.field === 'name')?.value || '', [chips]);
  const filterCourseId = useMemo(() => chips.find((c) => c.field === 'golf_course_id')?.value || '', [chips]);
  const filterDateFrom = useMemo(() => chips.find((c) => c.field === 'date_from')?.value || '', [chips]);
  const filterDateTo   = useMemo(() => chips.find((c) => c.field === 'date_to')?.value || '', [chips]);

  // Dynamic options for chip selects
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    listAllGolfCourses()
      .then((data) => setCourses(data.items || []))
      .catch(() => setCourses([]));
  }, []);

  const filters = useMemo(() => ({
    isArchived,
    name: filterName,
    courseId: filterCourseId,
    dateFrom: filterDateFrom,
    dateTo: filterDateTo,
  }), [isArchived, filterName, filterCourseId, filterDateFrom, filterDateTo]);

  const fetchFn = useCallback((cursor, { isArchived: archived, name, courseId, dateFrom, dateTo }) =>
    listBlocks({
      isArchived: archived,
      limit: LIST_PAGE_SIZE,
      cursor,
      ...(name     ? { name }                  : {}),
      ...(courseId ? { golfCourseId: courseId } : {}),
      ...(dateFrom ? { dateFrom }               : {}),
      ...(dateTo   ? { dateTo }                 : {}),
    }),
  []);

  const {
    items,
    total,
    loading,
    fetchError,
    selectedIds,
    setSelectedIds,
    page,
    totalPages,
    handleNext,
    handlePrev,
    handleArchive,
    handleUnarchive,
  } = useEntityList({
    fetchFn,
    archiveFn: archiveBlock,
    unarchiveFn: (id) => updateBlock(id, { is_archived: false }),
    filters,
  });

  const dynamicOptions = useMemo(() => ({
    golf_course_id: courses.map((c) => ({ value: String(c.id), label: c.name })),
  }), [courses]);

  const exportParams = {
    isArchived,
    name: filterName || undefined,
    golfCourseId: filterCourseId || undefined,
    dateFrom: filterDateFrom || undefined,
    dateTo: filterDateTo || undefined,
  };

  const addButton = (
    <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
      <ExportCSVButton
        exportFn={() => exportBlocksCSV(exportParams)}
        onError={setExportError}
        variant="list"
      />
      {isAdmin && <AddButton label="Add block" onClick={() => navigate('/blocks/new')} />}
    </div>
  );

  const filterPanel = (
    <div>
      <ChipBuilder
        chips={chips}
        onChipsChange={setChips}
        fields={CHIP_FIELDS}
        dynamicOptions={dynamicOptions}
      />
      {exportError && <p style={{ color: 'red', marginTop: '4px', fontSize: '0.875rem' }}>{exportError}</p>}
    </div>
  );

  return (
    <EntityList
      title="Blocks"
      columns={COLUMNS}
      items={items}
      total={total}
      loading={loading}
      errorMessage={fetchError}
      onRowClick={(item) => navigate(`/blocks/${item.id}`)}
      isAdmin={isAdmin}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      onArchive={handleArchive}
      filterPanel={filterPanel}
      page={page}
      totalPages={totalPages}
      onPrev={handlePrev}
      onNext={handleNext}
      addButton={addButton}
      showArchived={isArchived}
      onUnarchive={handleUnarchive}
    />
  );
}
